export type CountrySlug = "usa" | "uk" | "australia" | "italy" | "portugal" | "canada";

export interface Country {
  slug: CountrySlug;
  name: string;
  shortName: string;
  code: string; // ISO 3166-1 alpha-2 lowercase for flagcdn
  tagline: string;
  heroBlurb: string;
}

export const COUNTRIES: Country[] = [
  {
    slug: "usa",
    name: "United States",
    shortName: "USA",
    code: "us",
    tagline: "The American Dream, Decoded",
    heroBlurb:
      "Navigate US visas, H-1B, Green Card pathways, high-paying careers, and personal tax obligations with clarity.",
  },
  {
    slug: "uk",
    name: "United Kingdom",
    shortName: "UK",
    code: "gb",
    tagline: "Britain, End-to-End",
    heroBlurb:
      "Skilled Worker visas, Graduate Route, part-time jobs, HMRC tax rules, and cost-of-living guides for the UK.",
  },
  {
    slug: "australia",
    name: "Australia",
    shortName: "Australia",
    code: "au",
    tagline: "Down Under, Done Right",
    heroBlurb:
      "Subclass 189/190/482 pathways, verified casual jobs, ATO tax basics, and lifestyle intel for new arrivals.",
  },
  {
    slug: "italy",
    name: "Italy",
    shortName: "Italy",
    code: "it",
    tagline: "La Dolce Vita, Practical",
    heroBlurb:
      "Elective residency, digital nomad visa, work permits, Italian tax code, and the finer things — sorted.",
  },
  {
    slug: "portugal",
    name: "Portugal",
    shortName: "Portugal",
    code: "pt",
    tagline: "Europe's Softest Landing",
    heroBlurb:
      "D7, D8 digital nomad, and Golden Visa routes, NHR tax status, plus jobs and lifestyle in Lisbon & Porto.",
  },
  {
    slug: "canada",
    name: "Canada",
    shortName: "Canada",
    code: "ca",
    tagline: "The North, Made Simple",
    heroBlurb:
      "Express Entry, PNP, Study Permits, on-campus jobs, CRA tax basics, and settlement guides across Canada.",
  },
];

export const getCountry = (slug: string): Country | undefined =>
  COUNTRIES.find((c) => c.slug === slug);

export const flagUrl = (code: string, size: 40 | 80 | 160 | 320 | 640 | 1280 = 160) =>
  `https://flagcdn.com/w${size}/${code}.webp`;


export const roundFlagUrl = (code: string) =>
  `https://hatscripts.github.io/circle-flags/flags/${code}.svg`;

// Recommended articles per country (placeholder titles for sidebar/widgets)
export interface ArticleLink {
  title: string;
  slug: string;
  category: "Immigration" | "Jobs" | "Finance" | "Tax" | "Travel";
}

export const RECOMMENDED: Record<CountrySlug, ArticleLink[]> = {
  usa: [
    { title: "H-1B Visa 2026: A Complete Playbook", slug: "h1b-playbook", category: "Immigration" },
    { title: "Best Remote Part-Time Jobs in the US", slug: "us-part-time", category: "Jobs" },
    { title: "Filing Taxes as a Non-Resident Alien", slug: "us-tax-nra", category: "Tax" },
  ],
  uk: [
    { title: "UK Skilled Worker Visa Salary Thresholds", slug: "uk-skilled", category: "Immigration" },
    { title: "Graduate Route Jobs That Actually Pay", slug: "uk-grad-jobs", category: "Jobs" },
    { title: "Understanding UK PAYE for New Arrivals", slug: "uk-paye", category: "Tax" },
  ],
  australia: [
    { title: "Subclass 189 vs 190 vs 491: A Clear Guide", slug: "au-subclasses", category: "Immigration" },
    { title: "Casual Retail & Hospitality Jobs in Sydney", slug: "au-casual", category: "Jobs" },
    { title: "ATO Tax Return Basics for Migrants", slug: "au-ato", category: "Tax" },
  ],
  italy: [
    { title: "Italy's Digital Nomad Visa: Full Checklist", slug: "it-dnv", category: "Immigration" },
    { title: "English-Speaking Jobs in Milan & Rome", slug: "it-jobs", category: "Jobs" },
    { title: "Impatriati Regime: 70% Tax Relief Explained", slug: "it-impatriati", category: "Tax" },
  ],
  portugal: [
    { title: "D7 vs D8 Visa: Which One Fits You?", slug: "pt-d7-d8", category: "Immigration" },
    { title: "Remote Roles Hiring in Lisbon Right Now", slug: "pt-remote", category: "Jobs" },
    { title: "NHR 2.0 Tax Status: What Changed", slug: "pt-nhr", category: "Tax" },
  ],
  canada:  [
    { title: "Express Entry CRS Score: Boost Guide", slug: "ca-crs", category: "Immigration" },
    { title: "On-Campus Jobs for International Students", slug: "ca-campus", category: "Jobs" },
    { title: "CRA Tax Filing for New Permanent Residents", slug: "ca-cra", category: "Tax" },
  ],
};
